import { CrosspointDataProviderErrorHandler } from "./crosspoint-data-provider-error.handler.interface";
import type { ItemMapper } from './item-mapper.interface';
import type {CrosspointDataServiceFactoryConfig} from "./crosspoint-data-service-factory.type";

/**
 * Build a unique DataProvider ID from axis IDs
 *
 * Uses triple-colon separator to avoid collisions with axis IDs
 * that may contain single colons.
 *
 * @param axisIdX - TXAxisValue-axis identifier
 * @param axisIdY - TYAxisValue-axis identifier
 * @returns Combined provider ID
 *
 * @example
 * ```typescript
 * buildDataProviderId('time-axis', 'resource-axis');
 * // → 'time-axis:::resource-axis'
 *
 * buildDataProviderId('x', 'users:internal');
 * // → 'x:::users:internal'
 * ```
 */
export function buildDataProviderId(axisIdX: string, axisIdY: string): string {
  return `${axisIdX}:::${axisIdY}`;
}

/**
 * Parse a DataProvider ID back into axis IDs
 *
 * @param providerId - Combined provider ID
 * @returns Tuple of [axisIdX, axisIdY]
 * @throws Error if providerId is invalid format
 *
 * @example
 * ```typescript
 * parseDataProviderId('time-axis:::resource-axis');
 * // → ['time-axis', 'resource-axis']
 * ```
 */
export function parseDataProviderId(providerId: string): [string, string] {
  const parts = providerId.split(':::');
  if (parts.length !== 2) {
    throw new Error(
      `Invalid provider ID format: "${providerId}". ` +
      `Expected format: "axisIdX:::axisIdY"`
    );
  }
  return [parts[0], parts[1]];
}


/**
 * Represents a configuration object for a data provider with customizable axes, item mapping, and error handling.
 *
 * @template TXAxis Specifies the type for the TXAxisValue-axis.
 * @template TYAxis Specifies the type for the TYAxisValue-axis.
 * @template TItem Represents the type of items handled by the data provider.
 */
export interface CrosspointDataProviderConfiguration<TXAxis, TYAxis, TItem> {

  /**
   * TXAxisValue-axis identifier
   *
   * Must match an axis registered with the axis registry.
   * Used to look up the Axis for TXAxisValue-coordinate operations.
   */
  axisIdX: string;

  /**
   * TYAxisValue-axis identifier
   *
   * Must match an axis registered with the axis registry.
   * Used to look up the Axis for TYAxisValue-coordinate operations.
   */
  axisIdY: string;

  /**
   * Represents a service responsible for managing and processing item data,
   * which includes interactions between axes and items.
   *
   * @template TXAxis - The type representing the TXAxisValue-axis data structure or configuration.
   * @template TYAxis - The type representing the TYAxisValue-axis data structure or configuration.
   * @template TItem - The type representing the items managed by the service.
   */
  itemDataService: CrosspointDataServiceFactoryConfig<TXAxis, TYAxis, TItem>;

  /**
   * Item mapper for transforming domain items to matrix entries
   *
   * Responsible for:
   * - Extracting x/y ranges from items
   * - Handling multi-assignment (1 item → N entries)
   * - Generating unique IDs for matrix entries
   */
  itemMapper: ItemMapper<TXAxis, TYAxis, TItem>;

  /**
   * Optional error handler for fetch failures
   *
   * If not provided, uses DefaultErrorHandler which:
   * - SKIPs network errors (graceful degradation)
   * - ABORTs other errors (fail-fast)
   */
  errorHandler?: CrosspointDataProviderErrorHandler;
}

/**
 * Type alias for a configuration array
 *
 * Useful when defining multiple provider configurations.
 *
 * @template TItem - Domain item type
 * @template X - TXAxisValue-axis value type
 * @template Y - TYAxisValue-axis value type
 */
export type DataProviderConfigurations<TItem, X = unknown, Y = unknown> =
  Array<CrosspointDataProviderConfiguration<TItem, X, Y>>;
